import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import VoxelAlert from '../../../../components/voxel/Alert/VoxelAlert';
import VoxelLink from '../../../../components/voxel/Link/VoxelLink';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import { ProjectDuplicateStatusGq } from '../../../../graphql/projects/models/ProjectDuplicateStatusGq';
import { GetProjectStatusResultGq } from '../../../../graphql/projects/queries/useGetProjectStatusGq';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { TeamMenuChoices } from '../../../TeamPage/TeamPage';
import LoadingItem, { LoadingItemProps } from './LoadingItem/LoadingItem';
import ProjectStatusKeywordRequests from './ProjectStatusKeywordRequests';
import VoxelDivider from '../../../../components/voxel/VoxelDivider';

export interface dataProcessingInfoProps {
  projectStatus?: GetProjectStatusResultGq;
  blocked?: boolean;
  blockedAspects?: string[];
  projectId: number;
  setDeleteRequestId: React.Dispatch<React.SetStateAction<number>>;
}

export interface LoadingItems {
  kwLongtail: LoadingItemProps;
  kwGoogleAds: LoadingItemProps;
  kwVariants: LoadingItemProps;

  rankData: LoadingItemProps;
  translatingToEnglish: LoadingItemProps;
  rankCompetitors: LoadingItemProps;
  rankCalculation: LoadingItemProps;
  progressInitial: LoadingItemProps;

  tagAuto: LoadingItemProps;
  generatingTags: LoadingItemProps;

  scraping: LoadingItemProps;
  downloadAnalysis: LoadingItemProps;
  downloadKeyworrds: LoadingItemProps;
}

export const DataProcessingInfo: React.FC<dataProcessingInfoProps> = ({
  projectStatus,
  blocked,
  blockedAspects,
  projectId,
  setDeleteRequestId,
}) => {
  // Data fetching
  const loadingItems: LoadingItems = {
    kwLongtail: {
      label: `Retrieve google suggestions (${
        projectStatus?.longtailCount ?? 0
      } combinations processing)`,
    },
    kwGoogleAds: {
      label: `Add keywords ${
        projectStatus?.googleAdsProcessingCount > 0
          ? ` (${formatNumber(projectStatus?.googleAdsProcessingCount)} requests processing)`
          : ''
      }`,
    },
    kwVariants: {
      label: `Analyze close variants${
        projectStatus?.duplicateStatus === ProjectDuplicateStatusGq.QUEUED_FOR_LLM &&
        projectStatus?.duplicateLlmRequestCount > 0
          ? ` (${projectStatus?.duplicateLlmRequestCount} keywords left)`
          : ''
      }`,
    },
    rankData: {
      label: `Add rank data${
        projectStatus?.serpProcessingCount > 0
          ? ` (${projectStatus?.serpProcessingCount} keywords left)`
          : ''
      }`,
    },
    translatingToEnglish: {
      label: `Translating${
        projectStatus?.translatingCount > 0
          ? ` (${projectStatus?.translatingCount} keywords left)`
          : ''
      }`,
    },
    rankCompetitors: { label: 'Calculating top 10 traffic domains' },
    rankCalculation: { label: 'Calculate  domain ranks' },
    tagAuto: { label: 'Apply tags' },
    scraping: { label: 'Scrape URLs for content plan' },
    downloadAnalysis: { label: 'Generate analysis download' },
    downloadKeyworrds: { label: 'Generate keyword download' },
    progressInitial: { label: 'Generate initial progress results' },
    generatingTags: { label: projectStatus?.tagGenerationStep },
  };

  if (projectStatus?.longtailLoading) {
    loadingItems.kwLongtail.status = 'loading';
    loadingItems.kwGoogleAds.status = 'queued';
    loadingItems.kwVariants.status = 'queued';
  }

  if (projectStatus?.keywordSearchLoading) {
    loadingItems.kwGoogleAds.status = 'loading';
    loadingItems.kwVariants.status = 'queued';
  }

  if (projectStatus?.translatingCount) {
    loadingItems.translatingToEnglish.status = 'loading';
  }

  if (projectStatus?.duplicateStatus === ProjectDuplicateStatusGq.QUEUED_FOR_API_CALLS) {
    loadingItems.kwVariants.status = 'queued';
  }

  if (
    projectStatus?.duplicateStatus === ProjectDuplicateStatusGq.CREATING_API_CALLS ||
    projectStatus?.duplicateStatus === ProjectDuplicateStatusGq.QUEUED_FOR_STAT_DUPLICATES
  ) {
    loadingItems.kwVariants.status = 'loading';
  }

  if (projectStatus?.duplicateStatus === ProjectDuplicateStatusGq.QUEUED_FOR_LLM) {
    loadingItems.kwVariants.status = 'loading';
  }

  if (projectStatus?.includedRanksQueued) {
    loadingItems.rankData.status = 'queued';
    loadingItems.rankCompetitors.status = 'queued';
  }

  if (projectStatus?.rankingsLoading) {
    loadingItems.rankData.status = 'loading';
    loadingItems.rankCompetitors.status = 'queued';
  }

  if (!projectStatus?.rankingsLoading && projectStatus?.automaticDomainsLoading)
    loadingItems.rankCompetitors.status = 'loading';

  if (!projectStatus?.rankingsLoading && projectStatus?.domainRankCalculationsLoading)
    loadingItems.rankCalculation.status = 'loading';

  if (projectStatus?.autoTagging) loadingItems.tagAuto.status = 'loading';
  if (projectStatus?.scrapingUrls) loadingItems.scraping.status = 'loading';
  if (projectStatus?.generatingAnalysisDownload) loadingItems.downloadAnalysis.status = 'loading';
  if (projectStatus?.generatingKeywordDownload) loadingItems.downloadKeyworrds.status = 'loading';
  if (projectStatus?.transferringSerpToTracking) loadingItems.progressInitial.status = 'loading';
  if (projectStatus?.tagGenerationStep?.length) loadingItems.generatingTags.status = 'loading';

  return (
    <div className="w-[440px] p-3 px-5 flex flex-col gap-6">
      <div className="py-2 flex flex-col gap-3">
        {Object.values(loadingItems).map((item: LoadingItemProps) => {
          if (!item?.status) return null;
          return <LoadingItem key={item.label} label={item.label} status={item.status} />;
        })}
        {!Object.values(loadingItems).some((v) => v.status) && (
          <div className="flex flex-row items-center gap-3 ">
            <FontAwesomeIcon icon={faCheckCircle} size="lg" className="text-success-600/70" />
            <VoxelText> Project is fully processed</VoxelText>
          </div>
        )}
        <div className="pt-4">
          <VoxelDivider />
        </div>

        <ProjectStatusKeywordRequests
          projectId={projectId}
          setDeleteRequestId={setDeleteRequestId}
        />
      </div>
      {blocked && (
        <VoxelAlert title="App usage limitations" severity="error" iconPosition="top">
          <div className="flex flex-col gap-3">
            {blockedAspects.map((blockedAspect, index) => (
              <>
                {index > 0 ? ', ' : ''}
                {blockedAspect}
              </>
            ))}
            <span>
              Go to{' '}
              <VoxelLink href={'/team/' + TeamMenuChoices.Usage} openInNewTab>
                <span>
                  app usage <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="pr-1" />
                </span>
              </VoxelLink>{' '}
              for more information
            </span>
          </div>
        </VoxelAlert>
      )}
    </div>
  );
};

export default DataProcessingInfo;
